/**
 * ExplicitConsent Integration Layer
 * Rule: SDK -> Store bridge with UI feedback
 * This layer can use toast, store updates, and other UI concerns
 */
import i18next from 'i18next';
import { toast } from 'sonner';
import { useExplicitConsentStore } from '../../store/explicit-consent.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class ExplicitConsentIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch explicit consents and update store
     */
    async fetchExplicitConsents(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'asc', columnFilters = {}) {
        const store = useExplicitConsentStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) {
                        acc[`filter_${key}`] = value;
                    }
                    return acc;
                }, {})
            });

            // With createBaseController, response has:
            // { success, data (array), pagination, uniqueValues }
            const { data, pagination, uniqueValues } = response;
            store.setExplicitConsents(data || []);

            // Map pagination from backend response payload
            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page,
                    totalPages: pagination.totalPages,
                    totalItems: pagination.total,
                    itemsPerPage: pagination.pageSize,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            } else {
                // Fallback if pagination missing
                store.setPaginationData({
                    currentPage: page,
                    totalPages: Math.ceil((data?.length || 0) / pageSize) || 1,
                    totalItems: data?.length || 0,
                    itemsPerPage: pageSize,
                    hasNextPage: false,
                    hasPreviousPage: false,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Hub payload for the detail page (`GET /explicit-consent/:id`). Read path:
     * no toast, the page renders the error state.
     */
    async fetchExplicitConsentById(id) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }
        try {
            const response = await this.service.getById(id);
            if (response?.success === false) {
                return { success: false, error: response.message || response.error };
            }
            return { success: true, data: response?.data ?? null };
        } catch (error) {
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    /**
     * Create explicit consent
     */
    async createExplicitConsent(data) {
        const store = useExplicitConsentStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.create(data);

            toast.success(i18next.t('gdprObtainingExplicitConsent:messages.createSuccess', { defaultValue: 'Explicit consent created' }));

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update explicit consent
     */
    async updateExplicitConsent(id, data) {
        const store = useExplicitConsentStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.update(id, data);

            toast.success(i18next.t('gdprObtainingExplicitConsent:messages.updateSuccess', { defaultValue: 'Explicit consent updated' }));

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete explicit consent
     */
    async deleteExplicitConsent(id) {
        const store = useExplicitConsentStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            await this.service.delete(id);

            toast.success(i18next.t('gdprObtainingExplicitConsent:messages.deleteSuccess', { defaultValue: 'Explicit consent deleted' }));

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Batch delete explicit consents
     * @param {Array<string>} ids
     */
    async batchDeleteExplicitConsents(ids) {
        const store = useExplicitConsentStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.bulkDelete(ids);

            toast.success(i18next.t('gdprObtainingExplicitConsent:messages.bulkDeleteSuccess', {
                count: ids.length,
                defaultValue: `${ids.length} explicit consent records deleted`,
            }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Map error to user-friendly message
     */
    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }
}
